/**
 * TODO - preamble
 */
import React from 'react';
import { FormattedHTMLMessage, injectIntl } from 'react-intl';
import { produce } from 'immer';

import {
    VerificationStepsEnum,
    MilitaryStatusDefaultMessagesEnum,
    isFormFilled,
    hasFailedInstantMatch,
    setRef,
    resetRefs,
    logger,
    VerificationService,
    FormSelectChoice,
    Organization,
    InactiveMilitaryPersonalInfoViewModel,
    InactiveMilitaryPersonalInfoResponse,
    handleEmailOnKeyDown,
    submitForm,
    updateFieldValidationErrorsByFieldId,
    updateViewModelOrganization,
    getAvailableMilitaryStatuses,
    CountryComponentWrapper,
    HowDoesVerifyingWorkComponent as HowDoesVerifyingWork,
    LogoComponent,
    PhoneNumberComponent as PhoneNumber,
    FirstNameComponent as FirstName,
    LastNameComponent as LastName,
    EmailComponent as Email,
    BirthDateComponent as BirthDate,
    MilitaryStatusComponent as MilitaryStatus,
    BranchOfServiceComponent as BranchOfService,
    FormFooterComponent as FormFooter,
    MarketConsentWrapper,
    RewardsRemainingComponent,
    DischargeDateComponent as DischargeDate,
    getAvailableLocaleChoices,
    ChangeLocaleComponent,
    Locale,
} from '@sheerid/jslib';

import { updateMilitaryViewModel } from './militaryStepHelpers';
import { getStatusLabel } from '../../lib/utils/StepComponentHelpers/stepComponentHelpers';

 interface StepInactiveMilitaryPersonalInfoComponentProps {
   intl: any;
   verificationService: VerificationService;
 }

export const StepInactiveMilitaryPersonalInfo = ({
    intl,
    verificationService,
}: StepInactiveMilitaryPersonalInfoComponentProps) => {
    const viewModel = verificationService.viewModel as InactiveMilitaryPersonalInfoViewModel;
    const previousViewModel = verificationService.previousViewModel as InactiveMilitaryPersonalInfoViewModel;
    const { fieldValidationErrors } = verificationService;
    const verificationResponse = verificationService.verificationResponse as InactiveMilitaryPersonalInfoResponse;
    const failedInstantMatch = hasFailedInstantMatch(verificationResponse);
    const availableStatuses = getAvailableMilitaryStatuses(verificationService, intl);
    const localeChoices: FormSelectChoice<Locale, string>[] = getAvailableLocaleChoices(
        verificationService.programTheme,
        intl,
    );
    const defaultLocaleChoice: FormSelectChoice<Locale, string> = {
        value: 'en-US',
        label: 'English',
    };

    const updateInactiveMilitaryViewModel = (
        key: keyof InactiveMilitaryPersonalInfoViewModel,
        value: any,
    ) => {
        verificationService.updateViewModel(
            updateMilitaryViewModel(key, value, verificationService, availableStatuses),
        );
    };

    const submitStep = () => {
        const nextState = produce(viewModel, () => {});
        logger.info('StepMilitaryStatusComponent submitting form');
        verificationService.submitStep(
            VerificationStepsEnum.collectMilitaryStatus,
            nextState,
            verificationService.previousVerificationResponse || verificationService.verificationResponse,
        );
    };

    if (
        (viewModel.status && previousViewModel && previousViewModel.status !== viewModel.status)
     || (viewModel.status
    && verificationService.verificationResponse.currentStep
    === VerificationStepsEnum.collectMilitaryStatus)
    ) {
        submitStep();
    }

    const updateLocale = (localeChoice: FormSelectChoice<Locale, string>) => {
        const nextState: InactiveMilitaryPersonalInfoViewModel = produce(
            viewModel,
            (draft: InactiveMilitaryPersonalInfoViewModel) => {
                draft.localeChoice = localeChoice;
            },
        );
        verificationService.updateLocale(
            nextState,
            verificationService.programTheme,
            verificationService.verificationResponse.segment,
        );
    };

    // Refs should be reset to ensure the field order isn't affected by the previous step
    resetRefs();

    return (
        <div id="sid-step-inactive-military-personal-info" className="sid-l-container">
            {failedInstantMatch ? (
                <div className="sid-header">
                    <div className="sid-l-horz-center">
                        <LogoComponent verificationService={verificationService} />
                    </div>
                    <div className="sid-header__title sid-l-horz-center">
                        <FormattedHTMLMessage
                            id="step.personalInfo.tryAgain.title"
                            defaultMessage="Check Your Name & Birthdate"
                        />
                    </div>

                    <div className="sid-header__subtitle sid-l-horz-center sid-header__subtitle--error">
                        <FormattedHTMLMessage
                            id="step.personalInfo.tryAgain.subtitle"
                            defaultMessage="
                                 Enter your first and last name on the form as it appears on your military document(s).
                                 Keep in mind that may be your given name."
                        />
                    </div>
                </div>
            ) : (
                <div className="sid-header">
                    <div className="sid-l-horz-center">
                        <LogoComponent verificationService={verificationService} />
                    </div>
                    <div className="sid-header__title sid-l-horz-center">
                        <FormattedHTMLMessage
                            id="step.personalInfo.title"
                            defaultMessage="Unlock this Military-Only Offer"
                            tagName="h1"
                        />
                    </div>

                    <div className="sid-header__subtitle sid-l-horz-center">
                        <FormattedHTMLMessage
                            id="step.personalInfo.subtitle"
                            defaultMessage="Verify you're a member of the armed forces."
                        />
             &nbsp;
                        <HowDoesVerifyingWork verificationService={verificationService} />
                        <RewardsRemainingComponent verificationService={verificationService} />
                    </div>
                </div>
            )}

            <CountryComponentWrapper verificationService={verificationService} viewModel={viewModel} />

            {localeChoices.length > 1 ? (
                <ChangeLocaleComponent
                    options={localeChoices}
                    value={viewModel.localeChoice || defaultLocaleChoice}
                    isErrored={false}
                    onChange={(localeChoice: FormSelectChoice<Locale, string>) => updateLocale(localeChoice)}
                />
            ) : null}

            {availableStatuses ? (
                <MilitaryStatus
                    value={{
                        value: viewModel.status,
                        label: getStatusLabel(intl, viewModel.status, MilitaryStatusDefaultMessagesEnum),
                    }}
                    isErrored={!!fieldValidationErrors.status}
                    options={availableStatuses}
                    onChange={(status: FormSelectChoice) => {
                        updateInactiveMilitaryViewModel('status', status ? (status.value as string) : '');
                        updateFieldValidationErrorsByFieldId(
                            'status',
                            status ? (status.value as string) : '',
                            verificationService,
                        );
                    }}
                />
            ) : null}

            <BranchOfService
                isErrored={!!fieldValidationErrors.organization}
                onChange={(choice: Organization) => {
                    updateViewModelOrganization(choice, verificationService);
                    updateFieldValidationErrorsByFieldId('organization', choice, verificationService);
                }}
                value={{
                    value: (verificationService.viewModel as InactiveMilitaryPersonalInfoViewModel)
                        .organization.id,
                    label: (verificationService.viewModel as InactiveMilitaryPersonalInfoViewModel)
                        .organization.name,
                }}
                verificationService={verificationService}
                organizations={verificationService.orgList}
            />

            <div className="sid-names">
                <FirstName
                    value={viewModel.firstName}
                    isErrored={!!fieldValidationErrors.firstName}
                    onChange={(newValue: string) => {
                        updateInactiveMilitaryViewModel('firstName', newValue);
                        updateFieldValidationErrorsByFieldId('firstName', newValue, verificationService);
                    }}
                />

                <LastName
                    value={viewModel.lastName}
                    isErrored={!!fieldValidationErrors.lastName}
                    onChange={(newValue: string) => {
                        updateInactiveMilitaryViewModel('lastName', newValue);
                        updateFieldValidationErrorsByFieldId('lastName', newValue, verificationService);
                    }}
                />
            </div>

            <BirthDate
                isErrored={!!fieldValidationErrors.birthDate}
                errorId={fieldValidationErrors.birthDate}
                onChange={(newValue: string) => {
                    updateInactiveMilitaryViewModel('birthDate', newValue);
                    updateFieldValidationErrorsByFieldId('birthDate', newValue, verificationService);
                }}
                value={viewModel.birthDate}
            />

            <DischargeDate
                isErrored={!!fieldValidationErrors.dischargeDate}
                onChange={(newValue: string) => {
                    updateInactiveMilitaryViewModel('dischargeDate', newValue);
                    updateFieldValidationErrorsByFieldId('dischargeDate', newValue, verificationService);
                }}
                value={viewModel.dischargeDate}
            />

            <Email
                value={viewModel.email}
                isErrored={!!fieldValidationErrors.email}
                explanation={(
                    <FormattedHTMLMessage
                        id="emailExplanation"
                        defaultMessage="Personal email address is recommended"
                    />
                )}
                onChange={(newValue: string) => {
                    updateInactiveMilitaryViewModel('email', newValue);
                    updateFieldValidationErrorsByFieldId('email', newValue, verificationService);
                }}
                onKeyDown={(event: any) => handleEmailOnKeyDown(event)}
            />
            {verificationService.programTheme.isSmsNotifierConfigured
        || verificationService.programTheme.smsLoopEnabled ? (
                    <PhoneNumber
                        isRequired={!!verificationService.programTheme.smsLoopEnabled}
                        value={viewModel.phoneNumber}
                        isErrored={!!fieldValidationErrors.phoneNumber}
                        onChange={(newValue: string) => {
                            updateInactiveMilitaryViewModel('phoneNumber', newValue);
                            updateFieldValidationErrorsByFieldId('phoneNumber', newValue, verificationService);
                        }}
                    />
                ) : null}
            <MarketConsentWrapper
                verificationService={verificationService}
                isErrored={!!fieldValidationErrors.marketConsentValue}
                onChange={(newValue: any) => {
                    updateInactiveMilitaryViewModel('metadata', {
                        ...verificationService.viewModel.metadata,
                        marketConsentValue: newValue,
                    });
                    updateFieldValidationErrorsByFieldId('marketConsentValue', newValue, verificationService);
                }}
                viewModel={viewModel}
            />

            <div className="sid-form-region sid-submit-wrapper sid-l-space-top-md">
                <div className="sid-submit">
                    <button
                        id="sid-submit-btn-collect-info"
                        onClick={() => submitForm(
                            viewModel,
                            verificationService,
                            VerificationStepsEnum.collectInactiveMilitaryPersonalInfo,
                        )}
                        type="submit"
                        className={`sid-btn sid-btn--dark sid-l-full-width ${
                            !isFormFilled(viewModel, verificationService.formValidationOptions)
                                ? 'sid-btn--disabled-like'
                                : ''
                        }`}
                        aria-labelledby="verify-status-text"
                        ref={(button) => setRef('submitButton', button)}
                    >
                        <span id="verify-status-text">
                            {failedInstantMatch ? (
                                <FormattedHTMLMessage id="tryAgain" defaultMessage="Try Again" />
                            ) : (
                                <FormattedHTMLMessage
                                    id="verifyMyMilitaryStatus"
                                    defaultMessage="Verify My Military Status"
                                />
                            )}
                        </span>
                    </button>
                </div>
            </div>

            <FormFooter verificationService={verificationService} />
        </div>
    );
};

export const StepInactiveMilitaryPersonalInfoComponent = injectIntl(
    StepInactiveMilitaryPersonalInfo,
);
